import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import BranchSubNav from "@/components/branches/BranchSubNav";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { CalendarX, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { branchesApi, type Branch } from "@/lib/api/branches";
import { cn } from "@/lib/utils";

const DAYS = [
  { key: "MON", label: "Monday" },
  { key: "TUE", label: "Tuesday" },
  { key: "WED", label: "Wednesday" },
  { key: "THU", label: "Thursday" },
  { key: "FRI", label: "Friday" },
  { key: "SAT", label: "Saturday" },
  { key: "SUN", label: "Sunday" },
];

interface DayHours {
  open: string;
  close: string;
  closed: boolean;
}

interface Closure {
  date: string;
  reason: string;
}

const defaultHours = (): Record<string, DayHours> =>
  Object.fromEntries(
    DAYS.map((d) => [
      d.key,
      d.key === "SUN"
        ? { open: "09:00", close: "17:00", closed: true }
        : { open: "08:00", close: "18:00", closed: false },
    ])
  );

const BranchHoursPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { isTenantAdmin } = useAccessControl();
  const [branch, setBranch] = useState<Branch | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingFrom, setSavingFrom] = useState<"header" | "footer" | null>(null);
  const saving = savingFrom !== null;
  const [hours, setHours] = useState<Record<string, DayHours>>(defaultHours());
  const [closures, setClosures] = useState<Closure[]>([]);
  const [newClosure, setNewClosure] = useState<Closure>({ date: "", reason: "" });

  useEffect(() => {
    if (id) loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await branchesApi.getById(id!);
      setBranch(data);
      const settings = data.settings as any;
      if (settings?.hours) setHours(settings.hours);
      if (settings?.closures) setClosures(settings.closures);
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to load branch.");
      navigate("/setup/branches");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (from: "header" | "footer") => {
    try {
      setSavingFrom(from);
      const settings = { ...((branch?.settings as any) || {}), hours, closures };
      await branchesApi.update(id!, { settings });
      toast.success("Hours saved successfully.");
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to save hours.");
    } finally {
      setSavingFrom(null);
    }
  };

  const updateDay = (day: string, field: keyof DayHours, value: string | boolean) => {
    setHours((prev) => ({ ...prev, [day]: { ...prev[day], [field]: value } }));
  };

  const addClosure = () => {
    if (!newClosure.date) return;
    setClosures((prev) => [...prev, newClosure]);
    setNewClosure({ date: "", reason: "" });
  };

  const removeClosure = (index: number) => {
    setClosures((prev) => prev.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <BranchSubNav branch={branch} />

      <PageContainer width="wide">
        <PageHeader
          title="Operating Hours"
          description={`Set weekly schedule and special closures for ${branch?.name ?? "this branch"}`}
          breadcrumbItems={[
            { label: "Setup", href: "/setup" },
            { label: "Branches", href: "/setup/branches" },
            { label: branch?.name ?? "Branch", href: id ? `/setup/branches/${id}` : undefined },
            { label: "Hours" },
          ]}
          showSearch={false}
          titleSize="md"
          breadcrumbAlign="start"
          actions={
            isTenantAdmin ? (
              <Button onClick={() => handleSave("header")} disabled={saving} className="gap-2">
                {savingFrom === "header" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save Hours
              </Button>
            ) : undefined
          }
        />

        {/* Weekly schedule */}
        <Card className="p-6">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
            Weekly Schedule
          </h2>
          <div className="space-y-3">
            {DAYS.map((day) => {
              const d = hours[day.key] || { open: "08:00", close: "18:00", closed: false };
              return (
                <div
                  key={day.key}
                  className={cn(
                    "flex items-center gap-4 py-3 px-4 rounded-lg border",
                    d.closed ? "bg-gray-50 border-gray-200" : "bg-white border-gray-200"
                  )}
                >
                  <div className="w-28 flex-shrink-0">
                    <span
                      className={cn(
                        "text-sm font-medium",
                        d.closed ? "text-gray-400" : "text-gray-700"
                      )}
                    >
                      {day.label}
                    </span>
                  </div>

                  {d.closed ? (
                    <span className="text-sm text-gray-400 italic flex-1">Closed</span>
                  ) : (
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        type="time"
                        value={d.open}
                        onChange={(e) => updateDay(day.key, "open", e.target.value)}
                        disabled={!isTenantAdmin}
                        className="w-32 text-sm"
                      />
                      <span className="text-gray-400 text-sm">to</span>
                      <Input
                        type="time"
                        value={d.close}
                        onChange={(e) => updateDay(day.key, "close", e.target.value)}
                        disabled={!isTenantAdmin}
                        className="w-32 text-sm"
                      />
                    </div>
                  )}

                  {isTenantAdmin && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Checkbox
                        id={`closed-${day.key}`}
                        checked={d.closed}
                        onCheckedChange={(v) => updateDay(day.key, "closed", Boolean(v))}
                      />
                      <Label
                        htmlFor={`closed-${day.key}`}
                        className="text-sm text-gray-500 cursor-pointer"
                      >
                        Closed
                      </Label>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* Special closures */}
        <Card className="p-6">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
            Special Closures &amp; Holidays
          </h2>

          {closures.length === 0 ? (
            <p className="text-sm text-gray-400 italic mb-4">No special closures recorded.</p>
          ) : (
            <div className="space-y-2 mb-4">
              {closures.map((c, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between py-2 px-4 rounded-lg border border-gray-200 bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <CalendarX className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">
                      {new Date(c.date + "T00:00:00").toLocaleDateString(undefined, {
                        weekday: "short",
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                    {c.reason && <span className="text-sm text-gray-500">— {c.reason}</span>}
                  </div>
                  {isTenantAdmin && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeClosure(i)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {isTenantAdmin && (
            <div className="flex items-end gap-3 pt-2 border-t border-gray-100">
              <div className="flex-shrink-0">
                <Label className="text-xs text-gray-500 mb-1 block">Date</Label>
                <Input
                  type="date"
                  value={newClosure.date}
                  onChange={(e) => setNewClosure((p) => ({ ...p, date: e.target.value }))}
                  className="w-40 text-sm"
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs text-gray-500 mb-1 block">Reason (optional)</Label>
                <Input
                  placeholder="e.g. Public holiday, Staff training…"
                  value={newClosure.reason}
                  onChange={(e) => setNewClosure((p) => ({ ...p, reason: e.target.value }))}
                  className="text-sm"
                />
              </div>
              <Button
                onClick={addClosure}
                disabled={!newClosure.date}
                variant="outline"
                className="gap-2 flex-shrink-0"
              >
                <Plus className="w-4 h-4" />
                Add
              </Button>
            </div>
          )}
        </Card>

        {isTenantAdmin && (
          <div className="flex justify-end">
            <Button onClick={() => handleSave("footer")} disabled={saving} className="gap-2">
              {savingFrom === "footer" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save All Changes
            </Button>
          </div>
        )}
      </PageContainer>
    </AppLayout>
  );
};

export default BranchHoursPage;
